import React, {useMemo} from "react";
import {ALWAYS_LAST_KEY, dataGridSortMoney, dataGridSortNumber, dataGridSortText} from "../../../utils/dataGridSorting.js";
import {useTranslation} from "react-i18next";
import {
    useRenderCurrencyValue,
    useRenderNumber,
    useRenderPercentValue
} from "../../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../../utils/useUserSettings.js";
import {SensitiveMetric, useHasSensitiveMetric} from "../../../components/auth/rolesAndPermissions.js";
import {formatRatioMultiplier, marginPercentWithPositiveRevenue} from "../../../utils/reportingMetricRatios.js";
import {selectProfitabilityMetric} from "../../../utils/reportingPartialProfitability.js";
import {mediaFinanceScopeLabel, mediaSourceQualityBlocksMetrics} from "../../../utils/reportingMetricPresentation.js";

export function campaignGridProfitabilityValues(campaign, isMarketing, snapshotComplete = true) {
    const marketingPartial = campaign?.partialProfitabilityMarketing;
    const hasNoKnownMarketingPurchaseCost = isMarketing
        && marketingPartial?.knownLineCount === 0
        && marketingPartial?.knownPurchaseCost === null;
    const baseCampaign = hasNoKnownMarketingPurchaseCost
        ? {...campaign, spendGoodsGross: null}
        : campaign;

    if (campaign?.financeScope === "raw_provider" || mediaSourceQualityBlocksMetrics(campaign?.mediaSourceQuality)) {
        return {
            ...baseCampaign,
            pnoGross: null,
            pnoNet: null,
            roasGross: null,
            roasNet: null,
            poasGross: null,
            poasNet: null,
            profitGross: null,
            profitNet: null,
            profitPercentGross: null,
            profitPercentNet: null,
        };
    }
    if (!snapshotComplete) return baseCampaign;
    const suffix = isMarketing ? "Gross" : "Net";
    const profitKey = `profit${suffix}`;
    const poasKey = `poas${suffix}`;
    const complete = campaign?.[`purchaseCostComplete${suffix}`] === true;
    const profit = selectProfitabilityMetric({
        fullValue: campaign?.[profitKey],
        fullComplete: complete,
        partialProfitability: campaign?.[isMarketing ? "partialProfitabilityMarketing" : "partialProfitability"],
        partialField: "knownProfitAfterAllMarketing",
    });
    const poas = selectProfitabilityMetric({
        fullValue: campaign?.[poasKey],
        fullComplete: complete,
        partialProfitability: campaign?.[isMarketing ? "partialProfitabilityMarketing" : "partialProfitability"],
        partialField: "knownPoas",
    });

    if (complete) return baseCampaign;
    return {
        ...baseCampaign,
        [profitKey]: profit.value ?? null,
        [poasKey]: poas.value ?? null,
        [`profitPercent${suffix}`]: null,
    };
}

export function marketingProfitabilityTotalValues(partialProfitability, mediaSourceQuality = partialProfitability?.mediaSourceQuality) {
    const available = !mediaSourceQualityBlocksMetrics(mediaSourceQuality)
        && partialProfitability?.available === true && Number(partialProfitability.knownLineCount) > 0;
    const hasNoKnownMarketingPurchaseCost = partialProfitability?.knownLineCount === 0
        && partialProfitability?.knownPurchaseCost === null;
    return {
        ...(hasNoKnownMarketingPurchaseCost ? {spendGoodsGross: null} : {}),
        profitGross: available ? partialProfitability.knownProfitAfterAllMarketing ?? null : null,
        poasGross: available ? partialProfitability.knownPoas ?? null : null,
    };
}

export function channelMarketingProfitabilitySubtotal(channelData, isMarketing, accountCount, allCampaignsIncluded = false) {
    if (!isMarketing || accountCount !== 1 || !allCampaignsIncluded) return null;
    return marketingProfitabilityTotalValues(channelData?.partialProfitabilityMarketing, channelData?.mediaSourceQuality);
}

export const GridType = {
    MarketingOrders: 'MarketingOrders',
    MarketingProfitability: 'MarketingProfitability',
    MarketingLeads: 'MarketingLeads',
}

function sortNullableValue(firstRow, secondRow, columnName, ascending, compare) {
    if (firstRow[ALWAYS_LAST_KEY] === true || secondRow[ALWAYS_LAST_KEY] === true) {
        return compare(firstRow, secondRow, columnName, ascending);
    }

    const first = firstRow[columnName];
    const second = secondRow[columnName];
    const firstUnavailable = first === null || first === undefined;
    const secondUnavailable = second === null || second === undefined;

    if (firstUnavailable || secondUnavailable) {
        if (firstUnavailable && secondUnavailable) {
            return 0;
        }

        // Keep unavailable denominators at the end in either sort direction.
        return firstUnavailable ? 1 : -1;
    }

    return compare(firstRow, secondRow, columnName, ascending);
}

export function sortNullableNumber(firstRow, secondRow, columnName, ascending) {
    return sortNullableValue(firstRow, secondRow, columnName, ascending, dataGridSortNumber);
}

export function sortNullableMoney(firstRow, secondRow, columnName, ascending) {
    const first = firstRow[columnName];
    const second = secondRow[columnName];
    if (first === null || first === undefined || second === null || second === undefined) {
        return sortNullableValue(firstRow, secondRow, columnName, ascending, dataGridSortMoney);
    }

    return dataGridSortMoney(firstRow, secondRow, columnName, ascending);
}

export function useMarketingColumns(gridType, project, metricContext = undefined) {

    let {t, i18n} = useTranslation();

    let renderCurrencyValue = useRenderCurrencyValue();
    let renderPercentValue = useRenderPercentValue();
    let renderNumber = useRenderNumber();

    let [userSettings] = useUserSettings();
    let isMarketing = metricContext === undefined ? userSettings?.isMarketing === true : metricContext === 'marketing';
    let hasSensitiveMetric = useHasSensitiveMetric();
    let canSeeProfit = hasSensitiveMetric(SensitiveMetric.Profit, project);
    let canSeePurchasePrice = hasSensitiveMetric(SensitiveMetric.PurchasePrice, project);

    return useMemo(
        () => {

            let columns = [];

            columns.push({
                name: 'name',
                header: t('dashboard.source'),
                defaultFlex: 3,
                minWidth: 150,
                aggregate: false,
                sort: dataGridSortText,
            });

            columns.push({
                name: 'spendMarketing',
                header: t('dashboard.grid.spend-marketing'),
                desc: t('dashboard.grid.spend-marketing-long'),
                defaultFlex: 1.25,
                textAlign: 'end',
                minWidth: 100,
                render: (input) => {
                    const rawScopeLabel = mediaFinanceScopeLabel(input.data.financeScope, t, i18n?.language);
                    const label = rawScopeLabel ?? (mediaSourceQualityBlocksMetrics(input.data.mediaSourceQuality)
                        ? t("reporting_lab.media_source_quality.known_subtotal", {defaultValue: i18n?.language?.startsWith("en") ? "Known advertising cost subtotal" : "Známá část reklamních nákladů"})
                        : null);
                    return React.createElement("div", {className: "text-right"},
                        React.createElement("div", null, renderCurrencyValue(input.data.spendMarketing, true)),
                        label && React.createElement("div", {className: "text-xs text-slate-500 dark:text-slate-400"}, label),
                    );
                },
                sort: dataGridSortMoney,
            });

            columns.push({
                name: isMarketing ? 'spendGoodsGross' : 'spendGoodsNet',
                header: t('dashboard.grid.spend-goods'),
                desc: t('dashboard.grid.spend-goods-long'),
                defaultFlex: 1.25,
                textAlign: 'end',
                minWidth: 100,
                show: gridType === GridType.MarketingProfitability && canSeePurchasePrice,
                render: (input) => renderCurrencyValue(
                    isMarketing ? input.data.spendGoodsGross : input.data.spendGoodsNet,
                    true,
                ),
                sort: dataGridSortMoney,
            });

            if (gridType !== GridType.MarketingLeads) {
                columns.push({
                    name: isMarketing ? 'turnoverGross' : 'turnoverNet',
                    header: t('dashboard.turnover'),
                    defaultFlex: 1.25,
                    textAlign: 'end',
                    minWidth: 100,
                    render: (input) => renderCurrencyValue(
                        isMarketing ? input.data.turnoverGross : input.data.turnoverNet,
                        true
                    ),
                    sort: dataGridSortMoney,
                });

                columns.push({
                    name: isMarketing ? 'pnoGross' : 'pnoNet',
                    header: t('dashboard.grid.pno'),
                    desc: t('dashboard.grid.pno-long'),
                    defaultFlex: 0.75,
                    aggregate: false,
                    textAlign: 'end',
                    minWidth: 80,
                    render: (input) => mediaSourceQualityBlocksMetrics(input.data.mediaSourceQuality) || input.data.financeScope === "raw_provider" ? "-" : renderPercentValue(
                        isMarketing ? input.data.pnoGross : input.data.pnoNet,
                    ),
                    sort: dataGridSortNumber,
                });

                columns.push({
                    name: isMarketing ? 'roasGross' : 'roasNet',
                    header: t('dashboard.grid.roas'),
                    desc: t('dashboard.grid.roas-long'),
                    aggregate: false,
                    textAlign: 'end',
                    minWidth: 80,
                    render: (input) => mediaSourceQualityBlocksMetrics(input.data.mediaSourceQuality) || input.data.financeScope === "raw_provider" ? "-" : formatRatioMultiplier(
                        isMarketing ? input.data.roasGross : input.data.roasNet,
                        1,
                    ),
                    sort: dataGridSortNumber,
                });

                if (canSeeProfit) columns.push({
                    name: isMarketing ? 'poasGross' : 'poasNet',
                    header: t('dashboard.grid.poas'),
                    desc: t('dashboard.grid.poas-long'),
                    aggregate: false,
                    textAlign: 'end',
                    minWidth: 80,
                    render: (input) => mediaSourceQualityBlocksMetrics(input.data.mediaSourceQuality) || input.data.financeScope === "raw_provider" ? "-" : formatRatioMultiplier(
                        isMarketing ? input.data.poasGross : input.data.poasNet,
                        1,
                    ),
                    sort: dataGridSortNumber,
                });

                if (gridType === GridType.MarketingProfitability && canSeeProfit) {
                    columns.push({
                        name: isMarketing ? 'profitGross' : 'profitNet',
                        header: t('reporting_lab.kpi.profit'),
                        defaultFlex: 2,
                        textAlign: 'end',
                        minWidth: 80,
                        render: (input) => mediaSourceQualityBlocksMetrics(input.data.mediaSourceQuality) || input.data.financeScope === "raw_provider" ? renderCurrencyValue(null, true) : renderCurrencyValue(
                            isMarketing ? input.data.profitGross : input.data.profitNet,
                            true
                        ),
                        sort: dataGridSortMoney,
                    });

                    columns.push({
                        name: isMarketing ? 'profitPercentGross' : 'profitPercentNet',
                        header: t('reporting_lab.kpi.margin'),
                        defaultFlex: 1.25,
                        textAlign: 'end',
                        minWidth: 80,
                        show: gridType === GridType.MarketingProfitability,
                        render: (input) => mediaSourceQualityBlocksMetrics(input.data.mediaSourceQuality) || input.data.financeScope === "raw_provider" ? renderPercentValue(null, true) : renderPercentValue(
                            marginPercentWithPositiveRevenue(
                                isMarketing
                                    ? input.data.profitPercentGross
                                    : input.data.profitPercentNet,
                                isMarketing
                                    ? input.data.turnoverGross?.amount
                                    : input.data.turnoverNet?.amount,
                            ),
                            true
                        ),
                        sort: dataGridSortNumber,
                    });
               }

                columns.push({
                    name: isMarketing ? 'ordersGross' : 'ordersNet',
                    header: t('dashboard.grid.orders'),
                    desc: t('dashboard.grid.orders-long'),
                    defaultFlex: 0.6,
                    textAlign: 'end',
                    minWidth: 100,
                    render: (input) => renderNumber(
                        isMarketing ? input.data.ordersGross : input.data.ordersNet,
                    ),
                    sort: dataGridSortNumber,
                });

            }

            if (gridType === GridType.MarketingLeads) {
                columns.push({
                    name: 'totalLeads',
                    header: t('dashboard.leads'),
                    defaultFlex: 0.6,
                    textAlign: 'end',
                    minWidth: 80,
                    render: (input) => renderNumber(input.data.totalLeads),
                    sort: dataGridSortNumber,
                });

                columns.push({
                    name: 'verifiedLeads',
                    header: t('dashboard.verified-leads'),
                    defaultFlex: 0.6,
                    textAlign: 'end',
                    minWidth: 80,
                    render: (input) => renderNumber(input.data.verifiedLeads),
                    sort: dataGridSortNumber,
                });

                columns.push({
                    name: 'cpa',
                    header: t('dashboard.grid.cpa'),
                    desc: t('dashboard.grid.cpa-long'),
                    defaultFlex: 1,
                    textAlign: 'end',
                    minWidth: 100,
                    render: (input) => renderCurrencyValue(input.data.cpa, true),
                    sort: dataGridSortMoney,
                });
            }

            columns.push({
                name: 'clicks',
                header: t('dashboard.grid.clicks'),
                desc: t('dashboard.grid.clicks-long'),
                defaultFlex: 0.6,
                textAlign: 'end',
                minWidth: 80,
                render: (input) => renderNumber(input.data.clicks),
                sort: dataGridSortNumber,
            });

            columns.push({
                name: 'impressions',
                header: t('dashboard.grid.impressions'),
                desc: t('dashboard.grid.impressions-long'),
                defaultFlex: 1,
                textAlign: 'end',
                minWidth: 90,
                render: (input) => renderNumber(input.data.impressions),
                sort: dataGridSortNumber,
            });

            if (gridType !== GridType.MarketingProfitability) {
                columns.push({
                    name: 'cpc',
                    header: t('dashboard.grid.average-cpc'),
                    desc: t('dashboard.grid.average-cpc-long'),
                    defaultFlex: 0.8,
                    textAlign: 'end',
                    minWidth: 100,
                    render: (input) => mediaSourceQualityBlocksMetrics(input.data.mediaSourceQuality) || input.data.financeScope === "raw_provider" ? renderCurrencyValue(null, true) : renderCurrencyValue(input.data.cpc),
                    sort: sortNullableMoney,
                });

                columns.push({
                    name: 'cpm',
                    header: t('dashboard.grid.cpm'),
                    desc: t('dashboard.grid.cpm-long'),
                    defaultFlex: 0.8,
                    textAlign: 'end',
                    minWidth: 100,
                    render: (input) => mediaSourceQualityBlocksMetrics(input.data.mediaSourceQuality) || input.data.financeScope === "raw_provider" ? renderCurrencyValue(null, true) : renderCurrencyValue(input.data.cpm, true),
                    sort: sortNullableMoney,
                });

                columns.push({
                    name: 'ctr',
                    header: t('dashboard.grid.ctr'),
                    desc: t('dashboard.grid.ctr-long'),
                    defaultFlex: 0.8,
                    textAlign: 'end',
                    minWidth: 100,
                    render: (input) => renderPercentValue(input.data.ctr),
                    sort: sortNullableNumber,
                });
            }

            columns.push({
                name: isMarketing ? 'conversionRateGross' : 'conversionRateNet',
                header: t('dashboard.grid.conversion-rate'),
                desc: t('dashboard.grid.conversion-rate-long'),
                defaultFlex: 0.8,
                textAlign: 'end',
                minWidth: 110,
                render: (input) => renderPercentValue(
                    isMarketing ? input.data.conversionRateGross : input.data.conversionRateNet
                ),
                sort: sortNullableNumber,
            });

            return columns;
        },
        [gridType, userSettings, isMarketing, canSeeProfit, canSeePurchasePrice],
    );
}
